import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { ImpersonationBannerComponent } from '@app/impersonation/components/impersonation-banner.component';
import { resolveBackHref } from '@app/shared/utils/back-href.util';
import { IonicModule, IonRouterOutlet, Platform } from '@ionic/angular';

const HARDWARE_BACK_PRIORITY = 10;

@Component({
  selector: 'c-page-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, ImpersonationBannerComponent],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss',
})
export class PageHeaderComponent {
  readonly title = input.required<string>();

  /**
   * Renders the back button as a plain action that emits `back` instead of
   * navigating. For pages the router lands on directly: they have no stack to
   * pop, and going back from them means something other than "up one level"
   * (leaving registration means logging out, for instance).
   */
  readonly customBack = input<boolean>(false);
  readonly back = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly routerOutlet = inject(IonRouterOutlet, { optional: true });
  private readonly router = inject(Router);
  private readonly platform = inject(Platform);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly resolvedHref = computed(() => resolveBackHref(this.router));
  protected readonly isSubpage = computed(
    () => this.customBack() || !!this.routerOutlet?.canGoBack() || !!this.resolvedHref()
  );

  showImpersonationBanner = sessionStorage.getItem('actAs') !== null;

  constructor() {
    // Ionic's iOS page transition only crossfades toolbars it finds via the
    // direct-child selector `:scope > ion-header > ion-toolbar` on the routed
    // `.ion-page`. Because this component wraps the `<ion-header>`, that query
    // misses it and the header pops in instead of fading. Hoisting the header
    // to be a direct child of the page restores the transition. The host keeps
    // `display: contents`, so this has no visual effect.
    //
    // We find the page element by walking up to ion-router-outlet's direct child
    // instead of using closest('.ion-page'). When the app resumes from background
    // (e.g. push notification tap), Ionic hasn't yet applied the .ion-page class
    // to the new page container by the time afterNextRender fires. closest() would
    // then skip the correct element and land on a parent page (e.g. TabsPage),
    // hoisting the header to the wrong DOM level and causing it to persist across
    // tab switches.
    afterNextRender(() => {
      const header = this.host.nativeElement.querySelector('ion-header');
      if (!header) return;

      const outlet = this.host.nativeElement.closest('ion-router-outlet');
      if (!outlet) return;

      let page: Element | null = this.host.nativeElement;
      while (page && page.parentElement !== outlet) {
        page = page.parentElement;
      }

      if (page && header.parentElement !== page) {
        page.insertBefore(header, page.firstChild);
      }
    });

    afterNextRender(() => {
      if (this.customBack()) {
        this.handleHardwareBack();
      }
    });
  }

  /**
   * Routes Android's hardware back button to `back`, so it does what the
   * on-screen arrow does. The priority sits above the router outlet (0) and
   * below overlays (100), so an open modal still closes first.
   *
   * Ionic keeps earlier pages alive in its stack, so this page may be hidden
   * behind the one on screen. It only reacts while visible and otherwise
   * passes the event on — without that, pressing back on the phone step would
   * log out from the name step underneath it.
   */
  private handleHardwareBack(): void {
    const subscription = this.platform.backButton.subscribeWithPriority(
      HARDWARE_BACK_PRIORITY,
      (processNextHandler) => {
        if (this.host.nativeElement.closest('.ion-page-hidden')) {
          processNextHandler();
          return;
        }
        this.back.emit();
      }
    );
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
